import React from 'react'
import { graphql } from 'gatsby'
import { BREAKPOINT, COLOR } from '@farewill/ui/tokens'
import { Divider, Wrapper } from '@farewill/ui'
import { CLOUDINARY_ROOT_PATH } from '@farewill/ui/components/Image/constants'

import PATHS from 'paths'
import { TRUSTPILOT_RATING_OUT_OF_5, TRUSTPILOT_REVIEWS_TOTAL } from 'config'
import { PRODUCTS } from 'lib/products/constants'
import { TRACKING_TYPES } from 'lib/tracking/constants'
import { roundDownToNearestHundred } from 'lib/formatting/numbers'
import DefaultLayout from 'layouts/DefaultLayout'
import BreakpointSwitch from 'components/BreakpointSwitch'
import CoBrandedHeader from 'components/Header/Custom/CoBranded'
import GetStarted from 'views/wills/components/Overview/GetStarted'

import Hero from '../slices/Hero'
import Trustpilot from '../slices/Trustpilot'
import Pledges from '../slices/Pledges'
import FAQ from '../slices/FAQ'
import WillOptions from '../slices/WillOptions'
import GiftStories from '../slices/GiftStories'
import GiftSlider from '../slices/GiftSlider'

import type { GreyLabelLandingPageProps } from '../types'

const GreyLabelLandingPage = ({
  data: {
    page: {
      utmCampaign,
      utmMedium,
      partner,
      heroTitle,
      heroSubtitle,
      heroCtaText,
    },
    faqSection,
  },
}: GreyLabelLandingPageProps): React.ReactElement => (
  <DefaultLayout
    branding={partner.utmSource}
    title="Online Will Writing Service | Legal Will In 15 Minutes"
    description={`Our solicitor-approved guide helps you write a will online from the comfort of your own home. Rated ${TRUSTPILOT_RATING_OUT_OF_5} out of 5 by over ${roundDownToNearestHundred(
      TRUSTPILOT_REVIEWS_TOTAL
    )} customers on Trustpilot. National Will Writing Firm of the Year.`}
    meta={[
      {
        name: 'robots',
        content: 'noindex',
      },
    ]}
    customHeader={
      <CoBrandedHeader
        altText="Great Ormond Street Hospital Charity"
        assetDirectory="gosh"
        partnerLogoUrl="https://www.gosh.org/"
      />
    }
    product={PRODUCTS.WILLS}
    canonicalPath={PATHS.WILLS.OVERVIEW}
    headerColor={COLOR.WHITE}
    footerType="compact"
    utmParameters={{
      partnerId: partner?.partnerId || TRACKING_TYPES.NOT_SET,
      utmCampaign: utmCampaign || TRACKING_TYPES.NOT_SET,
      utmMedium: utmMedium || TRACKING_TYPES.NOT_SET,
      utmSource: partner?.utmSource || TRACKING_TYPES.NOT_SET,
      utmChannel: partner?.utmChannel || TRACKING_TYPES.NOT_SET,
    }}
  >
    <Wrapper background={COLOR.WHITE}>
      <Hero
        assetDirectory="gosh"
        title={heroTitle || 'Write your will for free today'}
        subtitle={heroSubtitle?.heroSubtitle}
        ctaText={heroCtaText || 'Let’s get started'}
        minHeight={650}
        imageTransformationParams={{
          small: 'ar_1.8,c_fill,f_auto,q_auto,g_faces,w_300',
          medium: 'ar_2.23,c_fill,f_auto,q_auto,g_faces,w_964',
          large: 'ar_1.05,c_fill,f_auto,q_auto,g_faces,h_650',
          extraLarge: 'ar_1.4,c_crop,f_auto,q_auto,g_faces,z_0.9/h_650',
        }}
        telephoneWillsEnabled={false}
      />

      <WillOptions partnerName="GOSH Charity" telephoneWillsEnabled={false} />

      <Wrapper padding={['XL', 0, 'XXL']} paddingFromL={[96, 0, 'XXL']}>
        <GiftStories
          stories={[
            {
              title: 'Help save childhood for more children like Maia',
              description:
                'Maia was born with a hole in her heart and had pioneering surgery at Great Ormond Street Hospital (GOSH) when she was just a few weeks old. Her mum, Meera, says: "GOSH has the best doctors, nurses and treatments to help you get through the most difficult time of your life."',
              source:
                'Maia, aged 10, had pioneering surgery when she was 2 months old',
              image:
                'com-assets/greylabel-landing-page-assets/gosh/case-study-1',
            },
            {
              title: 'Help give kids like Rowan the best chance',
              description:
                'Rowan had a bone marrow transplant at GOSH in 2022. He still visits the hospital regularly and has spent several birthdays there. His mum, Jo, says: "GOSH always makes it so special. He gets presents and balloons, and all the nurses come to wish him happy birthday.',
              source:
                'Rowan, aged 11, has been a patient at GOSH since he was 2',
              image:
                'com-assets/greylabel-landing-page-assets/gosh/case-study-2',
            },
          ]}
          title="Did you know? Around 30% of our income comes from gifts in Wills"
        />
      </Wrapper>

      <GiftSlider
        caption="This calculation uses an average estate value of £505,000 and GOSH Charity data from financial year 2024/25."
        partner="GOSH Charity"
        config={[
          {
            value: 0,
            label: '5%',
            text: '5% of an average estate could fund one month of spiritual care from our multi-faith Chaplaincy service. Pictured: St. Christopher’s Chapel at GOSH.',
            image:
              'ar_1,c_fill,f_auto,q_auto/com-assets/greylabel-landing-page-assets/gosh/gift-5',
          },
          {
            value: 25,
            label: '10%',
            text: '10% of an average estate could fund a researcher for a year looking to speed up recovery and improve the patient experience for children receiving a kidney transplant. Pictured: a GOSH researcher.',
            image:
              'ar_1,c_fill,f_auto,q_auto,w_300/com-assets/greylabel-landing-page-assets/gosh/gift-10',
          },
          {
            value: 50,
            label: '25%',
            text: '25% of an average estate could help fund three incubators for the smallest babies, ensuring they stay warm while allowing clinical staff and parents access to provide care and comfort. Pictured: Maia as a newborn.',
            image: 'com-assets/greylabel-landing-page-assets/gosh/gift-25',
            formatImagePath: ({ path, ext, width }) =>
              `${CLOUDINARY_ROOT_PATH}/ar_1,c_crop,g_xy_center,x_0,y_500,f_auto,q_auto,w_${width}/${path}.${ext}`,
          },
          {
            value: 75,
            label: '50%',
            text: '50% of an average estate could keep the Citizens Advice Service running for a year. Located within the hospital, CAS provides families with vital advice and support, often enabling children to return home safely and sooner. Pictured: Rowan at GOSH.',
            image:
              'ar_1,c_fill,f_auto,q_auto,w_300/com-assets/greylabel-landing-page-assets/gosh/gift-50',
          },
          {
            value: 100,
            label: '100%',
            text: "100% of an average estate could fund the GOSH Play team for almost 2 months. The team use different types of play to ease children's fears and make their hospital experience as comfortable as possible. Pictured: Emily at GOSH.",
            image:
              'ar_1,c_fill,f_auto,q_auto,w_300/com-assets/greylabel-landing-page-assets/gosh/gift-100',
          },
        ]}
      />

      <Wrapper container noTrim>
        <Trustpilot
          tag="charity"
          title="Farewill is trusted by many charity supporters"
        />
        <BreakpointSwitch breakAt={BREAKPOINT.M} aboveComponent={<Divider />} />
        <FAQ
          questions={faqSection.questions}
          imagePath="com-assets/greylabel-landing-page-assets/gosh/faq"
        />
        <BreakpointSwitch breakAt={BREAKPOINT.M} aboveComponent={<Divider />} />
        <Pledges
          title="A very special way to leave a lasting legacy"
          description="You can help to ensure no childhood is lost to serious illness — now and for generations to come."
          imagePath="fl_sanitize/cobranding-partner-logos/gosh/gosh-logo"
        />
      </Wrapper>

      <GetStarted
        partnerCampaignReferral
        telephoneWillsEnabled={false}
        title="Ready to write your will?"
      />
    </Wrapper>
  </DefaultLayout>
)

export const query = graphql`
  query GOSHGreyLabelPartnerLandingPage($slug: String!) {
    page: contentfulPartnerCampaignPage(slug: { eq: $slug }) {
      utmCampaign
      utmMedium
      heroTitle
      heroSubtitle {
        heroSubtitle
      }
      heroCtaText
      partner {
        partnerId
        telephoneWillsEnabled
        utmSource
        utmChannel
      }
      telephoneWillsEnabled
    }
    faqSection: contentfulFaqSection(
      contentful_id: { eq: "3zTcUWGWcbYdu2mWevrFO6" }
    ) {
      ...FaqSectionFields
    }
  }
`

export default GreyLabelLandingPage
